import React, { useMemo, useEffect, useRef, useState } from "react";
import { useI18n } from "@/i18n/hooks";
import { Button } from "../Base/Button";
import { useUserDataFlattened } from "../../modules";
import {
  useEnableDownloadAppFloatingOnHomeTop,
  useLoginRegisterConfig,
  usePlatformLogo,
} from "../../modules/homeDisplay/hooks";
import { useHomeDisplayStore } from "../../modules/homeDisplay/stores";
import { useCurrencySymbol } from "../../hooks/useUtils";
import { useNavigation, ROUTES } from "../../utils/navigation";
import { getCountryIdFromStorage } from "../../utils/countryCookie";
import { triggerPopup } from "../../utils/popupTriggers";
import { POPUP_CONFIGS } from "../../config/popupConfig";
import { openLoginRegisterPopup } from "../LoginRegisterPopup";
import { openDepositPopup } from "../DepositPopup";
import { PromoBubble } from "../PromoBubble";
import "./Header.scss";
import type { HeaderProps } from "./Header.types";
import type { RedirectConfigItem } from "../../modules/homeDisplay/types";
import CustomerIconImg from "@/assets/homePage/reskin/customer.webp";
import GameSearchIconImg from "@/assets/homePage/reskin/search.webp";

import InsuranceIconImg from "@/assets/homePage/reskin/insurance.webp";
import CountryRingImg from "@/assets/header/country-ring.webp";
import DefaultAvatarImg from "@/assets/profile/VIP0.webp";
import { Icon } from "../Icon";
import { utils } from "../..";
import { isDownloadAppBarSessionClosed } from "../../utils/downloadAppBarSession";

const FLAG_ICONS_CDN =
  "https://cdn.jsdelivr.net/npm/flag-icons@7.5.0/flags/1x1";

export const Header: React.FC<HeaderProps> = ({
  title,
  className = "",
  onNotificationClick,
}) => {
  const { t } = useI18n();
  const { navigate } = useNavigation();
  const { isAuthenticated, data } = useUserDataFlattened();
  const enableFloatingOnHomeTop = useEnableDownloadAppFloatingOnHomeTop();
  const balance = data?.balanceDetail?.balance ?? data?.balance;
  const currencySymbol = useCurrencySymbol();
  const [balanceVisible, setBalanceVisible] = useState(true);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const sync = () => {
      const h = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty("--header-height", `${h}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const platformLogo = usePlatformLogo();
  const logoSrc = utils.getImageUrl(platformLogo?.logoUrl || "");
  const loginRegisterConfig = useLoginRegisterConfig();
  const homeRegisterPromoText =
    loginRegisterConfig?.homeRegisterBtnFirstChargeText?.trim() ?? "";
  const redirectConfigs = useHomeDisplayStore(
    state => state.data?.redirectConfigs ?? []
  );
  const hasCountryList =
    Array.isArray(redirectConfigs) && redirectConfigs.length > 0;

  const { currentCountryDisplayName, currentCountryFlagCode } = useMemo(() => {
    const countryId =
      (utils.getObject("countryId") as string) ||
      getCountryIdFromStorage() ||
      (window._config?.countryId ?? "");
    const item = redirectConfigs.find(
      (c: RedirectConfigItem) =>
        (c.countryRedirectId ?? "").trim().toUpperCase() ===
        countryId.trim().toUpperCase()
    );
    const name =
      item?.countryName ?? item?.displayName ?? item?.name ?? item?.id ?? "";
    const raw = (item?.countryRedirectId ?? countryId ?? "").toString().trim();
    const flagCode = raw.length >= 2 ? raw.slice(0, 2).toLowerCase() : null;
    if (name) {
      return {
        currentCountryDisplayName: name,
        currentCountryFlagCode: flagCode,
      };
    }
    if (countryId.trim()) {
      const fromConfig = window._config?.countryList?.find(
        (c: { id?: string }) =>
          (c.id ?? "").trim().toUpperCase() === countryId.trim().toUpperCase()
      );
      const displayName =
        fromConfig?.name ?? fromConfig?.displayName ?? countryId.trim();
      const code = (countryId ?? "").slice(0, 2).toLowerCase();
      return {
        currentCountryDisplayName: displayName,
        currentCountryFlagCode: code || null,
      };
    }
    return { currentCountryDisplayName: "", currentCountryFlagCode: null };
  }, [redirectConfigs]);

  const [downloadAppBarVisible, setDownloadAppBarVisible] = useState(() => {
    if (utils.isApp() || utils.updateCheckIsPwa() || !enableFloatingOnHomeTop)
      return false;
    return !isDownloadAppBarSessionClosed();
  });
  useEffect(() => {
    const handler = (e: Event) =>
      setDownloadAppBarVisible((e as CustomEvent).detail);
    window.addEventListener("downloadAppBarChange", handler);
    return () => window.removeEventListener("downloadAppBarChange", handler);
  }, []);
  // 检查用户是否已注册过
  const hasRegistered = utils.getObject("has_registered");
  const handleLoginClick = () => {
    openLoginRegisterPopup({ defaultTab: "password" });
  };

  const handleAvatarClick = () => {
    navigate(ROUTES.PROFILE, { state: { fromBottomNav: true } });
  };

  const handleBalanceClick = () => {
    navigate(ROUTES.ACCOUNT_DETAILS);
  };

  const handleDepositClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    openDepositPopup();
  };

  const handleMessageClick = () => {
    navigate(`${ROUTES.MESSAGE_CENTER}?tabIndex=2`);
    if (onNotificationClick) {
      onNotificationClick();
    }
  };

  const handleServiceClick = () => {
    // 优先尝试打开 Tawk.to 聊天窗口（右下角嵌入式客服）
    if (
      (window as any).Tawk_API &&
      typeof (window as any).Tawk_API.maximize === "function"
    ) {
      (window as any).Tawk_API.maximize();
      return;
    }
    // 其次尝试打开 LiveChat
    if ((window as any).LiveChatWidget) {
      (window as any).LiveChatWidget.call("maximize");
      return;
    }
    // 降级：跳转到消息中心客服标签
    navigate(`${ROUTES.MESSAGE_CENTER}?tabIndex=1`);
  };

  const handleCountryClick = () => {
    triggerPopup(POPUP_CONFIGS.COUNTRY_LIST.id);
  };

  const displayTitle = title || t("common.title");

  return (
    <header
      ref={headerRef}
      className={`mobile-header ${isAuthenticated ? "mobile-header--authenticated" : ""} ${className}`}
      style={{
        marginTop: downloadAppBarVisible ? "3rem" : "0",
      }}
    >
      <div className="mobile-header__content">
        {isAuthenticated ? (
          /* 已登录：复用 auth-bar 胶囊，左侧用户信息，右侧客服/搜索 */
          <div className="mobile-header__auth-bar">
            {/* 用户头像 */}
            <div
              className="mobile-header__avatar"
              onClick={handleAvatarClick}
              role="button"
              tabIndex={0}
            >
              <img
                src={
                  data?.avatar
                    ? utils.getImageUrl(data.avatar)
                    : DefaultAvatarImg
                }
                alt="avatar"
                className="mobile-header__avatar-img"
              />
            </div>

            {/* 余额区块：币种符号 + 金额 + eye + 充值 */}
            <div className="mobile-header__balance-wrapper">
              <div className="mobile-header__balance" onClick={handleBalanceClick}>
                <span className="balance-currency">{currencySymbol}</span>
                <span className="balance-amount">
                  {balanceVisible
                    ? typeof balance === "number" && balance != null
                      ? balance.toString().split(".")[1]?.length > 2
                        ? balance.toFixed(2)
                        : balance
                      : "0"
                    : "***"}
                </span>
              </div>
              <img
                src={InsuranceIconImg}
                alt=""
                className="mobile-header__insurance-icon"
                draggable={false}
              />
              <button
                className="mobile-header__eye-btn"
                onClick={() => setBalanceVisible(v => !v)}
                aria-label={balanceVisible ? "Hide balance" : "Show balance"}
              >
                <Icon
                  name={balanceVisible ? "eye-closed" : "eye"}
                  size={16}
                  color="#5f4e41"
                />
              </button>
            </div>

            {/* 充值按钮 */}
            <Button
              variant="primary"
              size="sm"
              className="mobile-header__deposit-btn"
              onClick={handleDepositClick}
            >
              {t("common.deposit")}
            </Button>

            {/* 弹性空白，将客服/搜索推到右侧 */}
            <div className="mobile-header__spacer" />

            {/* 客服（深色图标，背景是白色胶囊） */}
            <button
              className="mobile-header__service-icon"
              onClick={() => navigate(`${ROUTES.MESSAGE_CENTER}?tabIndex=1`)}
              aria-label="Customer Service"
            >
              <img
                src={CustomerIconImg}
                alt=""
                className="mobile-header__auth-bar-icon-img"
                draggable={false}
              />
            </button>

            {/* 搜索（深色图标） */}
            <button
              className="mobile-header__search-icon"
              onClick={() => navigate("/game-search")}
              aria-label="Search"
            >
              <img
                src={GameSearchIconImg}
                alt=""
                className="mobile-header__auth-bar-icon-img"
                draggable={false}
              />
            </button>
          </div>
        ) : (
          /* 未登录：全宽 auth-bar，从左到右：Login | 注册 CTA | 客服 | 搜索 */
          <div className="mobile-header__auth-bar">
            <button
              className="mobile-header__login-btn"
              onClick={handleLoginClick}
            >
              {t("auth.login")}
            </button>
            <div className="mobile-header__register-btn-wrap">
              <PromoBubble text={homeRegisterPromoText} />
              <Button
                variant="primary"
                size="sm"
                className="mobile-header__register-btn"
                onClick={() =>
                  openLoginRegisterPopup({ defaultMode: "register" })
                }
              >
                {t("auth.registerToWinBig")}
              </Button>
            </div>
            <button
              className="mobile-header__service-icon"
              onClick={() => navigate(`${ROUTES.MESSAGE_CENTER}?tabIndex=1`)}
              aria-label="Customer Service"
            >
              <img
                src={CustomerIconImg}
                alt=""
                className="mobile-header__auth-bar-icon-img"
                draggable={false}
              />
            </button>
            <button
              className="mobile-header__search-icon"
              onClick={() => navigate("/game-search")}
              aria-label="Search"
            >
              <img
                src={GameSearchIconImg}
                alt=""
                className="mobile-header__auth-bar-icon-img"
                draggable={false}
              />
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
